import React from 'react';
import { Link } from 'react-router-dom';


export default function Terms() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
      {/* Terms Content */}
      <main className="pt-16 pb-20 bg-[#111010]">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-[#363232] border border-[#504A4A] rounded-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#EBFFEE]">Terms of Service</h1>
            <p className="text-center text-[#A9A3A3] mb-8">RollOps LLC</p>
            <p className="text-center text-[#A9A3A3] mb-12">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-[#A9A3A3] mb-8 leading-relaxed">
                Welcome to RollOps. These Terms of Service (&quot;Terms&quot;) govern your access to and use of the RollOps platform, 
                including our mobile application, website, and associated services (collectively, the &quot;Service&quot;). 
                By accessing or using the Service, you agree to be bound by these Terms.
              </p>

              {/* Founders Promo Section */}
              <section id="founders-promo" className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">Founders Promo Terms</h2>
                
                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">1. Promotional Pricing</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  The Founders Promo offers eligible gym owners a promotional rate of $20 per month for the first 100 customers 
                  who complete account setup and activate billing. This promotional rate applies to your RollOps platform subscription fee.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">2. Eligibility</h3>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-2">
                  <li>You must be a new RollOps customer (no previous active subscription)</li>
                  <li>You must be among the first 100 customers to complete account setup and activate billing</li>
                  <li>You must complete Stripe Connect onboarding and have an active Stripe account</li>
                  <li>Your account must remain in good standing</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">3. Promotional Period</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  The Founders Promo is limited to the first 100 customers. Once 100 customers have activated billing, 
                  the promotional rate will no longer be available to new customers. RollOps reserves the right to modify 
                  or discontinue this promotion at any time.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">4. Rate Changes</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  RollOps reserves the right to adjust subscription rates with 30 days advance notice. 
                  Founders Promo customers will be notified of any rate changes via email and in-app notifications. 
                  You may cancel your subscription at any time if you do not agree to rate changes.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">5. Billing</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  The promotional rate will be charged monthly via Stripe Connect. Billing begins upon successful 
                  completion of Stripe Connect onboarding and activation of your account. All payments are processed 
                  securely through Stripe, and RollOps does not store your full payment card information.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">6. Cancellation</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  You may cancel your subscription at any time through your account settings. Upon cancellation, 
                  you will retain access to the Service until the end of your current billing period. 
                  No refunds will be provided for partial billing periods.
                </p>
              </section>

              {/* Zero Fee Credit Card Processing Section */}
              <section id="zero-fee" className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">Zero Fee Credit Card Processing</h2>
                
                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">1. Processing Fees</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  RollOps offers zero-fee credit card processing for gym owners through our Stripe Connect integration. 
                  This means RollOps does not charge any additional processing fees on top of Stripe&apos;s standard processing rates. 
                  You pay only Stripe&apos;s standard processing fees (typically 2.9% + $0.30 per transaction).
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">2. Stripe Connect</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  All payment processing is handled through Stripe Connect Express accounts. When you set up billing, 
                  you will create your own Stripe Connect account. Payments from your members go directly to your Stripe account, 
                  and RollOps does not take any processing fees.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">3. Stripe Fees</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  While RollOps charges zero processing fees, Stripe charges standard processing fees for all transactions. 
                  These fees are:
                </p>
                <ul className="list-disc list-inside text-[#A9A3A3] mb-6 space-y-2">
                  <li>2.9% + $0.30 per successful card charge for online payments</li>
                  <li>Different rates may apply for in-person payments or international cards</li>
                  <li>Stripe&apos;s fees are subject to change by Stripe</li>
                </ul>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  For the most current Stripe pricing information, please visit{' '}
                  <a href="https://stripe.com/pricing" target="_blank" rel="noopener noreferrer" className="text-rollops-accent hover:text-[#EBFFEE] underline">
                    stripe.com/pricing
                  </a>.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">4. Payment Processing</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  All payment processing is handled securely by Stripe, a PCI-compliant payment processor. 
                  RollOps does not store or have access to your members&apos; full payment card information. 
                  You are responsible for ensuring your Stripe Connect account is properly configured and maintained.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">5. Payouts</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  Funds from member payments are deposited directly into your Stripe Connect account according to Stripe&apos;s 
                  payout schedule (typically 2-7 business days). RollOps does not hold or delay your funds. 
                  You can view payout schedules and manage your account through your Stripe Dashboard.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">6. Disputes and Refunds</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  All payment disputes and refunds are handled through Stripe. RollOps does not charge any fees for 
                  processing refunds or handling disputes. Standard Stripe dispute fees may apply as outlined in 
                  Stripe&apos;s terms of service.
                </p>
              </section>

              {/* General Terms Section */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold mb-4 text-rollops-accent">General Terms</h2>
                
                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">Acceptance of Terms</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  By using RollOps, you agree to these Terms and our Privacy Policy. If you do not agree, 
                  please do not use the Service.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">Modifications</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  RollOps reserves the right to modify these Terms at any time. We will notify you of material changes 
                  via email or in-app notification. Continued use of the Service after changes constitutes acceptance.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-[#EBFFEE]">Contact</h3>
                <p className="text-[#A9A3A3] mb-4 leading-relaxed">
                  If you have questions about these Terms, please contact us through the contact information 
                  provided in your account settings or visit our{' '}
                  <Link to="/#contact" className="text-rollops-accent hover:text-[#EBFFEE] underline">
                    contact page
                  </Link>.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
}
